import { useId, useState, type FormEvent } from "react";
import { OPPORTUNITY_PRODUCTS, OPPORTUNITY_VALUE_CENTS_MAX, type OpportunityResponse, type OpportunityView, type SellOpportunityBody } from "@gioia365/contracts";
import { useQuery } from "@tanstack/react-query";
import { appointmentOptionsQuery } from "../appointments/appointments-queries";
import { Button } from "../design/components/Button";
import { Dialog } from "../design/components/Dialog";
import { Reason } from "../design/components/Reason";
import { Select } from "../design/components/Select";
import { TextField } from "../design/components/TextField";
import { messageForError } from "../i18n/errors";
import { useT } from "../i18n/locale";
import { useLeadView } from "../leads/lead-queries";
import type { TabContext } from "../session/tab-context";
import { useChangeOpportunity } from "./opportunity-queries";

// «Venduto» dal riquadro della scheda (piano M6, R8, Task 25; mock-up `scheda-lead.html`, il modulo della vendita; questione 379,
// SEZ-10): «Cosa ha comprato» (Club o Percorso), «Valore in €» e «Chi ha chiuso» (a cui conta la vendita: di partenza chi registra,
// fra i colleghi attivi della finestra degli appuntamenti) sono obbligatori; tipo di contratto, offerta, data di inizio del pagamento
// e bonus facoltativi. Quel che manca si dice sul campo, senza mandare nulla; un rifiuto del server (la versione vecchia, chi ha
// chiuso non più attivo) resta nella finestra con il suo motivo (UX-7). Lo status del Lead non cambia (SEZ-9). Il modulo del mock-up
// ha anche «Note»: il contratto della vendita non le ha, e qui non ci sono.

type Props = {
  tab: TabContext;
  leadId: string;
  opportunity: OpportunityView;
  onClose: () => void;
  onSold: (response: OpportunityResponse) => void;
};

type Product = (typeof OPPORTUNITY_PRODUCTS)[number];
type Errors = { product?: string; value?: string; closer?: string };

/**
 * Il valore in euro come lo dà la casella dei numeri («2400», «2400.50»; i centesimi anche dopo la virgola) in centesimi, da 1
 * centesimo a un milione di euro come il server; altrimenti `null`.
 */
function euroCentsOf(text: string): number | null {
  const normalized = text.trim().replace(",", ".");
  if (!/^\d+(\.\d{1,2})?$/.test(normalized)) return null;
  const cents = Math.round(Number(normalized) * 100);
  return cents >= 1 && cents <= OPPORTUNITY_VALUE_CENTS_MAX ? cents : null;
}

export function SoldDialog({ tab, leadId, opportunity, onClose, onSold }: Props) {
  const t = useT();
  const ids = useId();
  const view = useLeadView();
  const options = useQuery(appointmentOptionsQuery(tab, leadId, view));
  const change = useChangeOpportunity(tab, leadId, view);
  const [product, setProduct] = useState<Product | "">("");
  const [value, setValue] = useState("");
  const [contract, setContract] = useState("");
  const [offer, setOffer] = useState("");
  const [paymentStart, setPaymentStart] = useState("");
  const [bonus, setBonus] = useState("");
  // `null`: la scelta di partenza, chi registra se è fra i colleghi attivi.
  const [closer, setCloser] = useState<string | null>(null);
  const [errors, setErrors] = useState<Errors>({});
  const [failure, setFailure] = useState<string | null>(null);
  const colleagues = options.data?.colleagues ?? [];
  const me = tab.kind === "membership" ? tab.membershipId : null;
  const shownCloser = closer ?? (colleagues.some((person) => person.id === me) ? (me ?? "") : "");

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (change.isPending) return;
    setFailure(null);
    const cents = euroCentsOf(value);
    const next: Errors = {
      ...(product === "" ? { product: t("opportunities.soldDialog.productMissing") } : {}),
      ...(cents === null ? { value: t("opportunities.soldDialog.valueMissing") } : {}),
      ...(shownCloser === "" ? { closer: t("opportunities.soldDialog.closerMissing") } : {}),
    };
    setErrors(next);
    if (product === "" || cents === null || shownCloser === "") return;
    const optional = (text: string) => (text.trim() === "" ? undefined : text.trim());
    const body: SellOpportunityBody = {
      product,
      valueCents: cents,
      ...(optional(contract) === undefined ? {} : { contractType: optional(contract) }),
      ...(optional(offer) === undefined ? {} : { offer: optional(offer) }),
      ...(paymentStart === "" ? {} : { paymentStartDate: paymentStart }),
      ...(optional(bonus) === undefined ? {} : { bonus: optional(bonus) }),
      closedByMembershipId: shownCloser,
      version: opportunity.version,
    };
    try {
      onSold(await change.mutateAsync({ opportunityId: opportunity.id, command: { action: "sold", body } }));
    } catch (refusal) {
      setFailure(messageForError(t, refusal));
    }
  }

  return (
    <Dialog open size="wide" title={t("opportunities.soldDialog.title")} onClose={onClose}>
      <form className="g-opportunity-form" onSubmit={(event) => void submit(event)} noValidate aria-busy={change.isPending}>
        {failure ? (
          <Reason tone="warning" role="alert">
            {failure}
          </Reason>
        ) : null}
        <div className="g-opportunity-form__grid">
          <Select
            id={`${ids}-product`}
            label={t("opportunities.soldDialog.product")}
            value={product}
            options={[{ value: "", label: t("opportunities.soldDialog.choose") }, ...OPPORTUNITY_PRODUCTS.map((item) => ({ value: item, label: t(`opportunities.products.${item}`) }))]}
            onChange={(next) => setProduct(OPPORTUNITY_PRODUCTS.find((item) => item === next) ?? "")}
            error={errors.product}
          />
          <TextField
            id={`${ids}-value`}
            label={t("opportunities.soldDialog.value")}
            type="number"
            inputMode="decimal"
            min={1}
            step={0.01}
            value={value}
            onChange={(event) => setValue(event.target.value)}
            error={errors.value}
          />
          <TextField
            id={`${ids}-contract`}
            label={t("opportunities.soldDialog.contract")}
            placeholder={t("opportunities.soldDialog.contractPlaceholder")}
            maxLength={100}
            value={contract}
            onChange={(event) => setContract(event.target.value)}
          />
          <TextField id={`${ids}-offer`} label={t("opportunities.soldDialog.offer")} maxLength={200} value={offer} onChange={(event) => setOffer(event.target.value)} />
          <TextField id={`${ids}-start`} label={t("opportunities.soldDialog.paymentStart")} type="date" value={paymentStart} onChange={(event) => setPaymentStart(event.target.value)} />
          <TextField id={`${ids}-bonus`} label={t("opportunities.soldDialog.bonus")} maxLength={200} value={bonus} onChange={(event) => setBonus(event.target.value)} />
          <Select
            id={`${ids}-closer`}
            label={t("opportunities.soldDialog.closer")}
            value={shownCloser}
            options={[{ value: "", label: t("opportunities.soldDialog.choose") }, ...colleagues.map((person) => ({ value: person.id, label: person.displayName }))]}
            onChange={setCloser}
            error={errors.closer}
          />
        </div>
        {options.isError ? (
          <div className="g-opportunity-form__problem">
            <Reason tone="warning" role="alert">
              {messageForError(t, options.error)}
            </Reason>
            <Button onClick={() => void options.refetch()}>{t("common.retry")}</Button>
          </div>
        ) : null}
        <div className="g-opportunity-form__actions">
          <Button variant="primary" type="submit" disabled={change.isPending}>
            {t("opportunities.soldDialog.save")}
          </Button>
          <Button onClick={onClose}>{t("common.cancel")}</Button>
        </div>
      </form>
    </Dialog>
  );
}
